import React from "react";
import { GROUPS } from "./constants";

// Top-of-page snapshot. Combines the internal-dependency report (from
// /api/health) with the external-services summary that the inner card pushes
// up via onSummaryChange. Single number per category so the operator sees the
// scoreboard at a glance before drilling into individual cards.
export function HealthOverviewBanner({ report, externalSummary }) {
  const deps = report?.dependencies || {};

  // Bucket each internal dep by the GROUPS definition above.
  const tally = (severity) => {
    const group = GROUPS.find((g) => g.severity === severity);
    if (!group) return { ok: 0, fail: 0, total: 0 };
    let ok = 0;
    for (const k of group.keys) if (deps[k]?.installed) ok += 1;
    return { ok, fail: group.keys.length - ok, total: group.keys.length };
  };

  const critical = tally("critical");
  const important = tally("important");
  const support = tally("support");
  const ext = externalSummary || { ok: 0, warn: 0, fail: 0, total: 0 };

  const cards = [
    {
      key: "external",
      label: "Servicios externos",
      ok: ext.ok,
      total: ext.total,
      bad: ext.fail + ext.warn,
      hint: ext.fail > 0
        ? `${ext.fail} con falla`
        : ext.warn > 0
          ? `${ext.warn} con advertencia`
          : "Todo conectado",
      tone: ext.fail > 0 ? "fail" : ext.warn > 0 ? "warn" : "ok",
    },
    {
      key: "critical",
      label: "Críticos del sistema",
      ok: critical.ok,
      total: critical.total,
      bad: critical.fail,
      hint: critical.fail > 0
        ? `${critical.fail} faltante${critical.fail > 1 ? "s" : ""}`
        : "OCR, PDFs y libs OK",
      tone: critical.fail > 0 ? "fail" : "ok",
    },
    {
      key: "important",
      label: "Importantes",
      ok: important.ok,
      total: important.total,
      bad: important.fail,
      hint: important.fail > 0
        ? `${important.fail} faltante${important.fail > 1 ? "s" : ""}`
        : "Chromium / CURP OK",
      tone: important.fail > 0 ? "warn" : "ok",
    },
    {
      key: "support",
      label: "Soporte",
      ok: support.ok,
      total: support.total,
      bad: support.fail,
      hint: support.fail > 0
        ? `${support.fail} faltante${support.fail > 1 ? "s" : ""}`
        : "Librerías Python OK",
      tone: support.fail > 0 ? "warn" : "ok",
    },
  ];

  const toneCls = {
    ok:   "border-emerald-500/30 bg-emerald-500/5",
    warn: "border-amber-500/30 bg-amber-500/5",
    fail: "border-red-500/30 bg-red-500/5",
  };
  const toneText = {
    ok: "text-emerald-300",
    warn: "text-amber-300",
    fail: "text-red-300",
  };

  return (
    <div
      data-testid="health-overview-banner"
      className="grid grid-cols-2 lg:grid-cols-4 gap-3"
    >
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={`overview-card-${c.key}`}
          className={`border rounded-xl p-4 ${toneCls[c.tone]}`}
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            {c.label}
          </p>
          <p className={`mt-1.5 text-2xl font-semibold ${toneText[c.tone]}`}>
            {c.ok}
            <span className="text-base text-slate-500 font-normal"> / {c.total}</span>
          </p>
          <p className={`text-[11px] mt-1 ${toneText[c.tone]} opacity-90`}>
            {c.hint}
          </p>
        </div>
      ))}
    </div>
  );
}
